import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { APP_ID, IS_HUB_LITE } from '@core/hubconfig';
import { EntityNameType } from '@core/interfaces/entity-name-type';
import { HubAuthService } from '@core/services/hub-auth/hub-auth.service';
import { HubRouterHelperService } from '@core/services/hub-router-helper/hub-router-helper.service';
import { LoadEntityNames } from '@core/store/entity-names/entity-names.actions';
import { environment } from '@env';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { ModalAction } from '@shared/components/hub-entity-action-modal/hub-entity-action-modal';
import { HubEntityActionModalComponent } from '@shared/components/hub-entity-action-modal/hub-entity-action-modal.component';
import { KeycloakEventType } from 'keycloak-angular';
import { EMPTY, merge, timer } from 'rxjs';
import { concatMap, distinctUntilChanged, filter, switchMap, take } from 'rxjs/operators';
import { GetUserApi } from '@core/store/user/user.actions';
import { UserState } from '@core/store/user/user.state';
import { LoadingService } from '@shared/components/hub-loading-spinner/loading.service';
import { Title } from '@angular/platform-browser';
import { AccountStore, olAppTitle } from '@ol/kit';

// WE do not need names just after the app is loaded, so we wait for 5 seconds to load the names.
const ENTITY_NAMES_LOAD_DELAY_MS = 5000;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit {
  private readonly modalService = inject(NgbModal);
  private readonly authService = inject(HubAuthService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly loadingService = inject(LoadingService);
  private readonly accountStore = inject(AccountStore);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * HubRouterHelperService is not used directly in a component, but it is important to have it here
   * it make proper items of previous and current urls used on the page.
   * Do not remove it
   */
  private readonly hubRouterHelperService = inject(HubRouterHelperService);

  private readonly title = inject(Title);

  constructor() {
    // The browser-tab title comes from the kit registry rather than the placeholder
    // <title> in index.html, so it tracks the installed @ol/kit the same way the
    // header logo and the favicon do. Route-level titles must append to it.
    this.title.setTitle(olAppTitle(APP_ID));
  }

  ngOnInit(): void {
    this.loadingService.show();
    this.hubRouterHelperService.previousUrl();
    timer(ENTITY_NAMES_LOAD_DELAY_MS)
      .pipe(take(1))
      .subscribe(() => {
        this.store.dispatch([
          new LoadEntityNames(EntityNameType.Connection),
          new LoadEntityNames(EntityNameType.Module),
          new LoadEntityNames(EntityNameType.Project),
        ]);
      });
    this.authService
      .getError$()
      .pipe(
        filter((error) => {
          return (
            (environment.enterprise &&
              ((error as unknown as { type: KeycloakEventType }).type ===
                KeycloakEventType.TokenExpired ||
                (error as unknown as { type: KeycloakEventType }).type ===
                  KeycloakEventType.AuthError)) ||
            (error as unknown as { message: string }).message === 'Login required'
          );
        }),
        concatMap(() => {
          const isAuthPage = this.router.url.startsWith('/auth');
          if (!IS_HUB_LITE && !isAuthPage) {
            const modalRef = this.modalService.open(HubEntityActionModalComponent, {
              centered: true,
            });

            modalRef.componentInstance.steps.set([
              {
                actionsButtons: [{ action: ModalAction.apply, label: 'Ok' }],
                title: 'Login Is Required',
                paragraphs: ["Seems like you haven't been here for a while..."],
              },
            ]);
            return merge(modalRef.dismissed, modalRef.closed);
          } else {
            return EMPTY;
          }
        }),
      )
      .subscribe(() => {
        this.authService.logout();
      });
    this.store.dispatch(new GetUserApi());

    // @ol/kit's AccountStore loaded the user during bootstrap, before the NGXS
    // user existed (GetUserApi resolves asynchronously above). Re-pull it into
    // the kit on every distinct user update — initial load, tenant switch, role
    // change — so the account shell / user dropdown never render stale identity.
    this.store
      .select(UserState.user)
      .pipe(
        filter(Boolean),
        distinctUntilChanged(),
        switchMap(() => this.accountStore.loadUser()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
